import { EntityNotFound } from '@common/EntityNotFound';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { GitInfo } from '@common/git/GitInfo';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useProjectState } from '@common/git/Project/hooks';
import { PageHeader } from '@common/Header/PageHeader';
import { DatasetDetailType, DatasetDetailQuery } from '@common/queries/dataset';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { titleCase } from '@common/utils/metadataUtils';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { TabPane, Stack, Tabs, Container, Button, Centered, Spinner, StackItem, Dialog } from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ExecutionServiceProvider } from '../../../context/execution';
import { useGlobalVariables } from '../../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../../context/mixpanel/context';
import { useExecutionServiceInstance } from '../../../data/executionHooks';
import { useGraphQlQuery } from '../../../data/util';
import { StructType } from '../../../schema/sparkTypes';
import { AttachClusterWithFabric } from '../../Workflow/AttachFabric/AttachClusterWithFabric';
import { ComponentRunContext, useRunDataContext } from '../../Workflow/AttachFabric/componentRunhook';
import DatasetInfo from '../components/DatasetInfo';
import { Expectation } from '../components/DatasetQuality';
import DatasetRelations from '../components/DatasetRelations';
import DatasetRuns from '../components/DatasetRuns';
import Priviliges from '../components/Priviliges';
import { DatasetPageLayout } from '../config/dataset';
import { Footer } from '../config/styled';
import { getPhysicalDataset } from '../util';
import { getMetadataDefaultCrumbs } from './MetadataDetailHeader';

const StyledStack = styled(Stack)`
  min-height: 0;
`;

const StyledStackItem = styled(StackItem)`
  width: 100%;
`;

export default function Page() {
  const parentEntity = Entity.Dataset;
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Dataset.tab.url>>();
  const uid = decodeURIComponent(params.uid as string);
  const tab = params.tab as string;
  const variables = { uid };
  const { data, isLoading } = useGraphQlQuery(
    [parentEntity, parentEntity + 'Detail', variables],
    DatasetDetailQuery,
    undefined,
    variables
  );
  const item = data?.Dataset;

  if (isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!item) return <EntityNotFound uid={uid} entity={Entity.Dataset} projectContext={true} />;
  return (
    <ProjectContextProvider projectId={item?.project?._id as string}>
      <CodeGenerationContextProvider>
        <DatasetDetail uid={uid} dataset={item} tab={tab} />
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
}
function DatasetDetail({
  uid,
  dataset,
  tab
}: {
  uid: string;
  dataset: NonNullable<DatasetDetailType['Dataset']>;
  tab: string;
}) {
  const parentEntity = Entity.Dataset;
  const [isSessionUsed, setSessionUsed] = useState(false);

  const [isExpectationChange, setExpectationChange] = useState(false);
  const { currentBranch: branch, isMainBranch: readOnly, projectId, projectName } = useProjectState();

  const globalConf = useGlobalVariables();
  const { datasetJson } = useMemo(() => getPhysicalDataset(dataset), [dataset]);
  const schema = datasetJson?.schema as unknown as StructType;

  const format = datasetJson?.format?.toLowerCase();

  const isExpectationDisabled = useCallback(() => {
    const isLivy = datasetJson?.datasetType === 'File' && !datasetJson?.path?.startsWith('dbfs');
    const isDatabricksFormat = ['livy', 'jdbc', 'snowflake', 'salesforce'].includes(format);
    return !globalConf.isDataQualityEnabled || isDatabricksFormat || isLivy;
  }, [globalConf, format, datasetJson]);

  const layout = DatasetPageLayout(!isExpectationDisabled());
  const [activeTab, setActiveTab] = useState(titleCase(tab) || layout.firstTab);

  useEffect(() => {
    if (isExpectationDisabled() && activeTab === 'expectations') {
      setActiveTab(layout.firstTab);
    }
  }, [activeTab, isExpectationDisabled, layout]);

  const executionService = useExecutionServiceInstance();
  const runData = useRunDataContext();

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      name: dataset.name,
      entity: parentEntity,
      url: window.location.href
    });
  }, [track, dataset.name, parentEntity]);

  const changeTab = async (tab: string) => {
    if (activeTab === 'Expectations' && !isExpectationChange) {
      const isSuccessDialogResult = await Dialog.warning({
        'data-test-id': 'leave-expectations-warning-dialog',
        title: 'Are you sure, you want to leave?',
        subTitle: 'Expectation changes will not be saved'
      });
      if (isSuccessDialogResult !== Dialog.ResponseAction.okButton) {
        return;
      }
    }
    setActiveTab(tab);
    track(EntityEvents.common.metadata.tabChange, {
      name: dataset.name,
      tab,
      entity: parentEntity,
      url: window.location.href
    });
  };

  const getTabDetail = (title: string, _entityName: string) => {
    if (title === 'Info') {
      return (
        <DatasetInfo
          projectID={projectId}
          isLoading={false}
          path={datasetJson?.path}
          format={format}
          branch={branch}
          schema={schema}
          readOnly={readOnly}
        />
      );
    } else if (title === 'Relations') {
      return <DatasetRelations projectId={dataset.project?._id as string} uid={uid} />;
    } else if (title === 'Privileges') {
      return <Priviliges />;
    } else if (title === 'Expectations') {
      return (
        <Expectation
          uid={uid}
          setExpectationChange={setExpectationChange}
          branch={branch as string}
          datasets={[{ schema, datasetId: uid, dataset: dataset.name as string }]}
        />
      );
    } else {
      return (
        <DatasetRuns
          isSessionUsed={isSessionUsed}
          entity={parentEntity}
          uid={uid}
          entityName={dataset.name}
          projectName={projectName}
        />
      );
    }
  };

  return (
    <ExecutionServiceProvider value={executionService}>
      <ComponentRunContext.Provider value={runData}>
        <Stack width='100%' height='100%' align='space-between' alignY='center'>
          <StyledStackItem grow='1'>
            <Stack width='100%' height='100%' align='center' alignY='center'>
              <DocumentTitle title={`Dataset:${dataset.name || ''}`} />
              <PageHeader crumbs={getMetadataDefaultCrumbs(Entity.Dataset, projectName as string, projectId, dataset)}>
                <Button
                  variant='primary'
                  disabled={readOnly}
                  elementType={readOnly ? 'button' : 'link'}
                  to={Private_Routes.Lineage_IDE.datasetOrPipeline.getUrl({
                    projectId: projectId,
                    type: Entity.Dataset.toLowerCase(),
                    datasetOrPipelineId: uid
                  })}
                  data-test-id='dataset-detail-open-lineage-viewer-button'>
                  Open Lineage Viewer
                </Button>
              </PageHeader>
              <StyledStack width='100%' height='100%' align='center' alignY='center'>
                <Container>
                  <Tabs
                    activeKey={activeTab}
                    onChange={(tab) => changeTab(tab)}
                    tabBarExtraContent={{
                      right: (
                        <AttachClusterWithFabric
                          projectId={projectId}
                          attachmentSource='ExecutionMetrics'
                          setSessionUsed={() => setSessionUsed(true)}
                          hide={activeTab !== 'Statistics'}
                        />
                      )
                    }}>
                    {layout.tabs.map((v) => {
                      const title = v.title;
                      return (
                        <TabPane
                          key={title}
                          tab={title}
                          elementType='link'
                          to={Private_Routes.Dataset.tab.getUrl({ uid, tab: title.toLowerCase() })}>
                          {getTabDetail(title, v.entity)}
                        </TabPane>
                      );
                    })}
                  </Tabs>
                </Container>
              </StyledStack>
            </Stack>
          </StyledStackItem>
          <Footer>{projectId ? <GitInfo projectId={projectId} /> : null}</Footer>
        </Stack>
      </ComponentRunContext.Provider>
    </ExecutionServiceProvider>
  );
}
